// Title formatting helpers — shared logic, THREE synced copies:
//   portal/lib/title-format.ts   (this file — display normalization)
//   src/lib/title-format.ts      (web CRM — normalize on property save)
//   mobile/utils/title-format.ts (mobile — normalize on property save)
// Keep the three files identical (feature-parity rule). ~26% of published
// titles are ALL-CAPS legacy data; the portal normalizes at display time so
// the stored value (what the agent typed) is never mutated retroactively.

/** Connector words that stay lowercase mid-title in Spanish. */
const SMALL_WORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'en', 'con', 'para', 'por',
  'y', 'o', 'u', 'a', 'al', 'un', 'una', 'unos', 'unas',
]);

/** Tokens that must stay fully uppercase (roman numerals, common acronyms). */
const KEEP_UPPER = /^(?:I{1,3}|IV|VI{0,3}|IX|XI{0,3}|XIV|XV|KM|GT|USA|PH|VIP|CES|CAES)$/;

/**
 * True when the string is written predominantly in uppercase — the signal we
 * use to decide a title needs normalizing. Short strings are left alone (an
 * 8-letter acronym heading could be legitimate).
 */
export function isMostlyUppercase(input: string): boolean {
  const letters = input.match(/[a-záéíóúüñ]/gi);
  if (!letters || letters.length < 8) return false;
  const upper = input.match(/[A-ZÁÉÍÓÚÜÑ]/g)?.length ?? 0;
  return upper / letters.length >= 0.7;
}

/**
 * Convert an ALL-CAPS (or mostly-caps) title to Spanish title case.
 * Mixed-case input is returned unchanged — intentional casing is respected.
 * Tokens containing digits (A10, KM.16, 2DA) keep their original casing.
 */
export function smartTitleCase(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, ' ');
  if (!isMostlyUppercase(trimmed)) return trimmed;

  const words = trimmed.split(' ');
  return words
    .map((word, i) => {
      if (/\d/.test(word)) return word; // A10, KM.16, 3RA — leave as typed
      // Dotted abbreviations (C.C., S.A., E.E.U.U.) stay fully uppercase.
      if (/^(?:[a-záéíóúüñ]\.){1,}[a-záéíóúüñ]?\.?$/i.test(word)) return word.toUpperCase();
      const bare = word.replace(/[^A-ZÁÉÍÓÚÜÑa-záéíóúüñ]/g, '').toUpperCase();
      if (KEEP_UPPER.test(bare)) return word.toUpperCase();
      const lower = word.toLocaleLowerCase('es-GT');
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toLocaleUpperCase('es-GT') + lower.slice(1);
    })
    .join(' ');
}

/**
 * Truncate at a word boundary, never mid-word. No ellipsis — SERP titles and
 * meta descriptions read better cut clean (Google adds its own …).
 */
export function truncateAtWord(input: string, max: number): string {
  const s = input.trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max + 1);
  const lastSpace = cut.lastIndexOf(' ');
  // Only respect the boundary when it doesn't throw away too much.
  const out = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : s.slice(0, max);
  return out.replace(/[\s,.;:·|-]+$/, '');
}
